import React, { useState, useEffect, useCallback, useRef } from 'react';
import ErpiaSelect from './ErpiaSelect';
import { confirmErpia } from './ConfirmDialog';
import APIService from '../services/api';
import CvEditeur from './CvEditeur';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;

// ⚠️ Routes montées sur /cv-prep (PAS de préfixe /api).
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

const RUN_STEPS = [
  { key: 'extraction', label: 'Extraction' },
  { key: 'analyse', label: 'Analyse' },
  { key: 'generation', label: 'Génération' },
  { key: 'argumentaire', label: 'Argumentaire' },
  { key: 'rescore', label: 'Re-score' },
];

const STATUS = {
  DRAFT:                  { label: 'Brouillon',       bg: 'var(--chip-bg)',           fg: 'var(--text-2)' },
  SENT_TO_CANDIDATE:      { label: 'Envoyé candidat', bg: 'rgba(56,132,255,0.14)',    fg: '#3b82f6' },
  VALIDATED_BY_CANDIDATE: { label: 'Validé candidat', bg: 'rgba(45,200,110,0.16)',    fg: '#16a34a' },
  READY_FOR_SUBMISSION:   { label: 'Prêt au dépôt',   bg: 'rgba(45,200,110,0.22)',    fg: '#15803d' },
};

const GAP_CAT = {
  REFORMULABLE: { label: 'Reformulable', bg: 'rgba(45,200,110,0.14)', fg: '#16a34a', icon: 'check2-circle', hint: 'Déjà traité par la reformulation.' },
  A_CREUSER:    { label: 'À creuser',    bg: 'rgba(217,119,6,0.14)',  fg: '#b45309', icon: 'question-circle', hint: 'Question à poser au candidat.' },
  GAP_REEL:     { label: 'Écart réel',   bg: 'rgba(226,35,26,0.12)',  fg: 'var(--brand)', icon: 'exclamation-octagon', hint: 'Ne sera jamais comblé — point de la fiche candidat.' },
};

const DIFF_TYPES = ['REFORMULATION', 'REORDER', 'CONDENSE', 'EXPAND', 'MANUAL'];

// Un chemin de diff (« profile », « experience[2].tasks[1] ») touche-t-il cette section racine ?
const pathTouches = (diffSections, path) =>
  diffSections.some(s => s === path || s.startsWith(`${path}[`) || s.startsWith(`${path}.`));

const fmtScore = (v) => (v == null || Number.isNaN(Number(v))) ? '—' : Math.round(Number(v));

// ————————————————————————————————————————————————————————————————
// Colonne CV structuré (source ou optimisé), surlignage des sections changées
function CvColumn({ cv, diffSections, side }) {
  if (!cv) return <div style={{ fontSize: '12.5px', color: 'var(--text-faint)', padding: '10px' }}>—</div>;
  const changed = side === 'after';
  const mark = (path) => (changed && pathTouches(diffSections, path))
    ? { background: 'rgba(226,35,26,0.07)', borderLeft: '2px solid var(--brand)', paddingLeft: '8px', borderRadius: '0 6px 6px 0' }
    : {};
  const H = ({ children }) => <div style={{ fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', margin: '14px 0 6px' }}>{children}</div>;
  return (
    <div style={{ fontSize: '12.5px', color: 'var(--text)', lineHeight: 1.5 }}>
      <div style={{ ...mark('name') }}>
        <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-strong)' }}>{cv.name || '—'}</div>
        <div style={{ ...mark('title'), fontSize: '12.5px', color: 'var(--text-2)' }}>{cv.title || ''}</div>
      </div>
      {cv.profile != null && (<><H>Profil</H><div style={mark('profile')}>{cv.profile || '—'}</div></>)}

      {Array.isArray(cv.skills?.technical_skills) && cv.skills.technical_skills.length > 0 && (
        <><H>Compétences techniques</H>
        <div style={{ ...mark('skills'), display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
          {cv.skills.technical_skills.map((s, i) => <span key={i} style={{ fontSize: '11.5px', padding: '2px 8px', borderRadius: '6px', background: 'var(--chip-bg)', color: 'var(--text-2)' }}>{s}</span>)}
        </div></>
      )}

      {Array.isArray(cv.experience) && cv.experience.length > 0 && (
        <><H>Expérience</H>
        {cv.experience.map((e, i) => (
          <div key={i} style={{ ...mark(`experience[${i}]`), marginBottom: '11px', paddingBottom: '9px', borderBottom: i < cv.experience.length - 1 ? '1px solid var(--hair)' : 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
              <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{e.position || '—'}</span>
              <span style={{ fontSize: '11px', color: 'var(--text-3)', flex: 'none' }}>{[e.start_date, e.end_date].filter(Boolean).join(' – ')}</span>
            </div>
            <div style={{ fontSize: '11.5px', color: 'var(--text-2)' }}>{[e.company, e.location].filter(Boolean).join(' · ')}</div>
            {Array.isArray(e.tasks) && e.tasks.length > 0 && (
              <ul style={{ margin: '5px 0 0', paddingLeft: '16px' }}>
                {e.tasks.map((t, j) => <li key={j} style={{ ...mark(`experience[${i}].tasks[${j}]`), marginBottom: '2px' }}>{t}</li>)}
              </ul>
            )}
            {Array.isArray(e.technologies) && e.technologies.length > 0 && (
              <div style={{ marginTop: '5px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {e.technologies.map((t, j) => <span key={j} style={{ fontSize: '10.5px', padding: '1px 6px', borderRadius: '5px', background: 'var(--ghost-bg)', color: 'var(--text-3)' }}>{t}</span>)}
              </div>
            )}
          </div>
        ))}</>
      )}

      {Array.isArray(cv.education) && cv.education.length > 0 && (
        <><H>Formation</H>
        {cv.education.map((ed, i) => (
          <div key={i} style={{ marginBottom: '5px' }}>
            <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{[ed.degree, ed.field].filter(Boolean).join(' — ')}</span>
            <span style={{ fontSize: '11.5px', color: 'var(--text-2)' }}>{ed.institution ? ` · ${ed.institution}` : ''}{ed.end_date ? ` (${ed.end_date})` : ''}</span>
          </div>
        ))}</>
      )}

      {Array.isArray(cv.languages) && cv.languages.length > 0 && (
        <><H>Langues</H>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {cv.languages.map((l, i) => <span key={i} style={{ fontSize: '11.5px', color: 'var(--text-2)' }}>{l.name}{l.proficiency ? ` — ${l.proficiency}` : ''}</span>)}
        </div></>
      )}
    </div>
  );
}

// ————————————————————————————————————————————————————————————————
function Stepper({ step, status, error, onRetry, retrying }) {
  const idx = RUN_STEPS.findIndex(s => s.key === step);
  return (
    <div style={{ padding: '30px 4px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0', maxWidth: '640px', margin: '0 auto' }}>
        {RUN_STEPS.map((s, i) => {
          const done = status !== 'error' && (i < idx || status === 'done');
          const active = status !== 'error' && i === idx && status !== 'done';
          const failed = status === 'error' && i === idx;
          const color = done ? '#16a34a' : failed ? 'var(--brand)' : active ? 'var(--accent)' : 'var(--text-faint)';
          return (
            <React.Fragment key={s.key}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '7px', flex: 'none' }}>
                <div style={{ width: '30px', height: '30px', borderRadius: '50%', border: `2px solid ${color}`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700, background: 'var(--panel)' }}>
                  {done ? <BI icon="check-lg" /> : active ? <BI icon="arrow-repeat" style={{ animation: 'ats-spin .8s linear infinite' }} /> : failed ? <BI icon="x-lg" /> : i + 1}
                </div>
                <span style={{ fontSize: '11px', fontWeight: 600, color }}>{s.label}</span>
              </div>
              {i < RUN_STEPS.length - 1 && <div style={{ flex: 1, height: '2px', background: done ? '#16a34a' : 'var(--hair)', margin: '0 2px', marginBottom: '20px' }} />}
            </React.Fragment>
          );
        })}
      </div>
      {status === 'error' && (
        <div style={{ textAlign: 'center', marginTop: '22px' }}>
          <div style={{ fontSize: '13px', color: 'var(--brand)', marginBottom: '12px' }}><BI icon="exclamation-triangle" /> {error || 'La préparation a échoué.'}</div>
          <button className="ats-btn ats-btn-primary" onClick={onRetry} disabled={retrying} style={{ padding: '9px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, opacity: retrying ? 0.6 : 1 }}>
            <BI icon="arrow-repeat" style={retrying ? { animation: 'ats-spin .8s linear infinite' } : undefined} /> {retrying ? 'Relance…' : 'Relancer'}
          </button>
        </div>
      )}
      {status !== 'error' && (
        <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '12.5px', color: 'var(--text-2)' }}>Optimisation en cours… (30 à 90 s)</div>
      )}
    </div>
  );
}

// ————————————————————————————————————————————————————————————————
// Écran principal : prend un prep chargé et affiche diff / anomalies / gaps / workflow
function PrepView({ prep, prepId, onReload, showNotification }) {
  const [diffFilter, setDiffFilter] = useState('ALL');
  const [editing, setEditing] = useState(null); // { section, value }
  const [editorOpen, setEditorOpen] = useState(false);
  const [regen, setRegen] = useState(null); // { section, instructions }
  const [busy, setBusy] = useState(false);
  const [emailPrompt, setEmailPrompt] = useState(null); // email en attente pour send-to-candidate
  const [emailVal, setEmailVal] = useState('');
  const [docxError, setDocxError] = useState(null); // erreur 400 explicite de generate-docx

  const diff = Array.isArray(prep.diff) ? prep.diff : [];
  const diffSections = diff.filter(d => diffFilter === 'ALL' || d.type === diffFilter).map(d => d.section);
  const anomalies = Array.isArray(prep.anomalies) ? prep.anomalies : [];
  const gaps = Array.isArray(prep.gaps) ? prep.gaps : [];
  const argu = Array.isArray(prep.argumentaire) ? prep.argumentaire : [];
  const st = STATUS[prep.status] || STATUS.DRAFT;
  const validated = prep.status === 'VALIDATED_BY_CANDIDATE' || prep.status === 'READY_FOR_SUBMISSION';

  const call = async (path, opts, okMsg) => {
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/cv-prep/${prepId}${path}`, opts);
      const j = await r.json().catch(() => ({}));
      if (r.status === 400 && j.need_email) { setEmailPrompt(true); return { needEmail: true }; }
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      if (okMsg) showNotification?.(okMsg, 'success');
      await onReload();
      return j;
    } catch (e) { showNotification?.(`Échec : ${e.message}`, 'error'); return null; }
    finally { setBusy(false); }
  };

  const warnIfValidated = async () => !validated || await confirmErpia({ message: 'Ce CV est déjà validé par le candidat. Le modifier le fera retomber en brouillon (DRAFT) et il devra être re-validé. Continuer ?' });

  const saveSection = async () => {
    if (!(await warnIfValidated())) return;
    await call('/sections', { method: 'PATCH', headers: jsonH(), body: JSON.stringify({ section: editing.section, value: editing.value }) }, 'Section mise à jour');
    setEditing(null);
  };
  const regenerateSection = async () => {
    if (!(await warnIfValidated())) return;
    await call('/regenerate-section', { method: 'POST', headers: jsonH(), body: JSON.stringify({ section: regen.section, instructions: regen.instructions }) }, 'Section régénérée');
    setRegen(null);
  };
  const sendToCandidate = async (email) => {
    const res = await call('/send-to-candidate', { method: 'POST', headers: jsonH(), body: JSON.stringify(email ? { email } : {}) }, email ? 'Envoyé au candidat' : undefined);
    if (res && !res.needEmail) { setEmailPrompt(null); setEmailVal(''); if (res.link) showNotification?.(`Lien : ${res.link}`, 'success'); }
  };
  const markReady = async () => {
    const j = await call('/status', { method: 'POST', headers: jsonH(), body: JSON.stringify({ status: 'READY_FOR_SUBMISSION' }) });
    if (j) showNotification?.(`Prêt au dépôt — enregistré au nom de ${j.ready_by_name || 'l\'utilisateur connecté'}.`, 'success');
  };
  // generate-docx peut renvoyer un 400 explicite (« Rendu du template TAT échoué… »,
  // « Template TAT ou générateur python indisponible… ») plutôt qu'un DOCX hors charte :
  // on affiche le message en bandeau avec « Réessayer », pas seulement en toast.
  const generateDocx = async () => {
    // UN SEUL CV PAR BESOIN : le document généré devient LE CV du besoin —
    // s'il y en a déjà un (déposé à la main ou généré avant), il est remplacé.
    const ok = await confirmErpia({
      title: 'Générer le CV format entreprise ?',
      message: 'Le CV généré devient le CV de ce besoin : s’il y en a déjà un — déposé ou généré —, il est remplacé. Il n’y a qu’un seul CV par besoin.',
      confirmLabel: 'Générer et remplacer', tone: 'primary',
    });
    if (!ok) return;
    setDocxError(null);
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/cv-prep/${prepId}/generate-docx`, { method: 'POST', headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { setDocxError(j.error || `HTTP ${r.status}`); return; }
      showNotification?.('DOCX généré (dans les documents du candidat) — le score de ce besoin se recalcule automatiquement.', 'success');
      await onReload();
    } catch (e) { setDocxError(e.message || 'Erreur réseau'); }
    finally { setBusy(false); }
  };
  const rescore = () => call('/rescore', { method: 'POST', headers: authH() }, 'Re-scoré');

  const copyArgu = () => { try { navigator.clipboard.writeText(argu.map(a => `• ${a}`).join('\n')); showNotification?.('Argumentaire copié', 'success'); } catch { /* noop */ } };

  const sectTitle = { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: '10px' };
  const cardBox = { background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px' };
  const miniBtn = { fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 600, padding: '4px 9px', borderRadius: '6px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text-2)', cursor: 'pointer' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Bandeau workflow + scores */}
      <div style={{ ...cardBox, display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, padding: '4px 11px', borderRadius: '7px', background: st.bg, color: st.fg }}>{st.label}</span>
        {prep.status === 'READY_FOR_SUBMISSION' && (prep.ready_by_name || prep.ready_at) && (
          <span style={{ fontSize: '11.5px', color: 'var(--text-2)' }}>
            par {prep.ready_by_name || 'un collaborateur'}{prep.ready_at ? ` le ${new Date(prep.ready_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} à ${new Date(prep.ready_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : ''}
          </span>
        )}
        {/* generated_document_id référence un position_document — on ne s'en sert que comme booléen.
            Il peut être obsolète si le document a été retiré du positionnement : re-générer suffit. */}
        {prep.generated_document_id != null && (
          <span title="Si le document a été retiré du positionnement, cette information peut être obsolète — re-cliquez « Générer le DOCX »."
            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', background: 'rgba(45,200,110,0.14)', color: '#16a34a', cursor: 'default' }}>
            <BI icon="file-earmark-word" /> DOCX généré
          </span>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '13px' }}>
          <span style={{ color: 'var(--text-2)' }}>Score ERPIA</span>
          <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontWeight: 700, color: 'var(--text-3)' }}>{fmtScore(prep.score_before)}</span>
          <BI icon="arrow-right" style={{ fontSize: '11px', color: 'var(--text-faint)' }} />
          <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontWeight: 800, color: '#16a34a', fontSize: '15px' }}>{fmtScore(prep.score_after)}</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
          <button style={miniBtn} disabled={busy} onClick={rescore}><BI icon="arrow-repeat" /> Re-scorer</button>
          {prep.status === 'DRAFT' && <button className="ats-btn ats-btn-primary" disabled={busy} onClick={() => sendToCandidate()} style={{ ...miniBtn, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)' }}><BI icon="send" /> Envoyer au candidat</button>}
          {validated && prep.status !== 'READY_FOR_SUBMISSION' && <button className="ats-btn ats-btn-primary" disabled={busy} onClick={markReady} style={{ ...miniBtn, border: 'none', background: '#15803d', color: '#fff' }}><BI icon="check2-all" /> Marquer prêt au dépôt</button>}
          <button style={miniBtn} disabled={busy} onClick={generateDocx}><BI icon="file-earmark-word" /> Générer le DOCX</button>
        </div>
      </div>

      {docxError && (
        <div style={{ ...cardBox, display: 'flex', alignItems: 'flex-start', gap: '10px', border: '1px solid rgba(226,35,26,0.30)', background: 'rgba(226,35,26,0.06)' }}>
          <BI icon="file-earmark-x" style={{ color: 'var(--brand)', flex: 'none', marginTop: '2px' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-strong)' }}>Génération du DOCX impossible</div>
            <div style={{ fontSize: '12.5px', color: 'var(--text-2)', marginTop: '2px' }}>{docxError}</div>
          </div>
          <button style={{ ...miniBtn, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', flex: 'none' }} disabled={busy} onClick={generateDocx}>
            <BI icon="arrow-repeat" style={busy ? { animation: 'ats-spin .8s linear infinite' } : undefined} /> Réessayer
          </button>
          <button style={{ ...miniBtn, flex: 'none' }} onClick={() => setDocxError(null)}><BI icon="x-lg" /></button>
        </div>
      )}

      {emailPrompt && (
        <div style={{ ...cardBox, display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12.5px', color: 'var(--text-2)' }}>Aucun email sur la fiche — adresse du candidat :</span>
          <input value={emailVal} onChange={e => setEmailVal(e.target.value)} type="email" autoFocus placeholder="prenom.nom@email.com"
            style={{ flex: 1, minWidth: '200px', padding: '7px 10px', borderRadius: '7px', border: '1px solid var(--input-border)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '13px', outline: 'none' }} />
          <button style={{ ...miniBtn, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)' }} disabled={busy || !/.+@.+\..+/.test(emailVal)} onClick={() => sendToCandidate(emailVal.trim())}>Envoyer</button>
          <button style={miniBtn} onClick={() => setEmailPrompt(null)}>Annuler</button>
        </div>
      )}

      {/* Anomalies */}
      {anomalies.length > 0 && (
        <div style={{ ...cardBox }}>
          <div style={sectTitle}>Anomalies</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {anomalies.map((a, i) => {
              const blocking = a.severity === 'BLOCKING';
              const invention = /invention/i.test(a.type || '');
              return (
                <div key={i} style={{ display: 'flex', gap: '9px', padding: '9px 11px', borderRadius: '8px', background: blocking ? 'rgba(226,35,26,0.08)' : 'rgba(217,119,6,0.08)', border: `1px solid ${blocking ? 'rgba(226,35,26,0.25)' : 'rgba(217,119,6,0.22)'}` }}>
                  <BI icon={invention ? 'shield-exclamation' : blocking ? 'exclamation-octagon-fill' : 'exclamation-triangle-fill'} style={{ color: blocking ? 'var(--brand)' : '#b45309', flex: 'none', marginTop: '1px' }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)' }}>{blocking ? 'Bloquant' : 'Avertissement'}{a.type ? ` · ${a.type}` : ''}{invention ? ' — contenu inventé retiré par le garde-fou' : ''}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '2px' }}>{a.detail}</div>
                    {a.questionCandidat && <div style={{ fontSize: '11.5px', color: 'var(--text-3)', marginTop: '3px', fontStyle: 'italic' }}>Question candidat : {a.questionCandidat}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Diff côte à côte */}
      <div style={{ ...cardBox }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <div style={{ ...sectTitle, marginBottom: 0 }}>CV — Source → Optimisé</div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
            {['ALL', ...DIFF_TYPES].map(t => (
              <button key={t} onClick={() => setDiffFilter(t)} style={{ ...miniBtn, background: diffFilter === t ? 'var(--accent)' : 'var(--ghost-bg)', color: diffFilter === t ? 'var(--accent-fg)' : 'var(--text-2)', border: diffFilter === t ? 'none' : '1px solid var(--input-border)' }}>{t === 'ALL' ? 'Tout' : t}</button>
            ))}
          </div>
        </div>
        {editorOpen ? (
          <CvEditeur prep={prep} prepId={prepId} candidatNom={prep.structured_cv?.name || ''}
            showNotification={showNotification} onReload={onReload} onClose={() => setEditorOpen(false)} />
        ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-3)', marginBottom: '8px' }}>SOURCE</div>
            <CvColumn cv={prep.structured_source} diffSections={diffSections} side="before" />
          </div>
          <div style={{ borderLeft: '1px solid var(--hair)', paddingLeft: '18px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--brand)', marginBottom: '8px' }}>OPTIMISÉ (FORMAT ENTREPRISE)</div>
            <CvColumn cv={prep.structured_cv} diffSections={diffSections} side="after" />
          </div>
        </div>
        )}
        {/* Édition de section */}
        <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid var(--hair)', display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
          {!editorOpen && (
            <button style={{ ...miniBtn, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)' }} onClick={() => setEditorOpen(true)}>
              <BI icon="pencil-square" /> Éditer le CV
            </button>
          )}
          <button style={miniBtn} onClick={() => setEditing({ section: 'profile', value: prep.structured_cv?.profile || '' })}><BI icon="pencil" /> Modifier une section</button>
          <button style={miniBtn} onClick={() => setRegen({ section: 'profile', instructions: '' })}><BI icon="arrow-clockwise" /> Régénérer une section</button>
          {validated && <span style={{ fontSize: '11.5px', color: '#b45309', alignSelf: 'center' }}><BI icon="exclamation-triangle" /> Modifier retombera en brouillon</span>}
        </div>
      </div>

      {/* Édition inline */}
      {editing && (
        <div style={{ ...cardBox }}>
          <div style={sectTitle}>Modifier — section</div>
          <input value={editing.section} onChange={e => setEditing(s => ({ ...s, section: e.target.value }))} placeholder="ex. profile, experience[0].tasks[1]"
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 11px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--surface)', color: 'var(--text)', fontFamily: "'IBM Plex Mono',monospace", fontSize: '12.5px', marginBottom: '8px', outline: 'none' }} />
          <textarea value={editing.value} onChange={e => setEditing(s => ({ ...s, value: e.target.value }))} rows={4}
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '13px', resize: 'vertical', outline: 'none' }} />
          <div style={{ display: 'flex', gap: '7px', marginTop: '9px' }}>
            <button style={{ ...miniBtn, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)' }} disabled={busy} onClick={saveSection}>Enregistrer</button>
            <button style={miniBtn} onClick={() => setEditing(null)}>Annuler</button>
          </div>
        </div>
      )}
      {regen && (
        <div style={{ ...cardBox }}>
          <div style={sectTitle}>Régénérer — section</div>
          <input value={regen.section} onChange={e => setRegen(s => ({ ...s, section: e.target.value }))} placeholder="ex. profile, experience[2]"
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 11px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--surface)', color: 'var(--text)', fontFamily: "'IBM Plex Mono',monospace", fontSize: '12.5px', marginBottom: '8px', outline: 'none' }} />
          <textarea value={regen.instructions} onChange={e => setRegen(s => ({ ...s, instructions: e.target.value }))} rows={3} placeholder="Instructions (ex. « insister sur le lead technique, condenser »)"
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '13px', resize: 'vertical', outline: 'none' }} />
          <div style={{ display: 'flex', gap: '7px', marginTop: '9px' }}>
            <button style={{ ...miniBtn, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)' }} disabled={busy} onClick={regenerateSection}><BI icon="arrow-clockwise" /> Régénérer</button>
            <button style={miniBtn} onClick={() => setRegen(null)}>Annuler</button>
          </div>
        </div>
      )}

      {/* Gaps */}
      {gaps.length > 0 && (
        <div style={{ ...cardBox }}>
          <div style={sectTitle}>Écarts (gaps)</div>
          {['REFORMULABLE', 'A_CREUSER', 'GAP_REEL'].map(cat => {
            const items = gaps.filter(g => g.categorie === cat);
            if (!items.length) return null;
            const c = GAP_CAT[cat];
            return (
              <div key={cat} style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '6px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '6px', background: c.bg, color: c.fg }}><BI icon={c.icon} /> {c.label}</span>
                  <span style={{ fontSize: '11px', color: 'var(--text-faint)' }}>{c.hint}</span>
                </div>
                {items.map((g, i) => (
                  <div key={i} style={{ padding: '8px 11px', borderRadius: '8px', border: '1px solid var(--hair)', marginBottom: '6px' }}>
                    <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)' }}>{g.critere}</div>
                    {g.detail && <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '2px' }}>{g.detail}</div>}
                    {g.reformulation && <div style={{ fontSize: '11.5px', color: '#16a34a', marginTop: '3px' }}><BI icon="arrow-return-right" /> {g.reformulation}</div>}
                    {g.questionCandidat && <div style={{ fontSize: '11.5px', color: '#b45309', marginTop: '3px', fontStyle: 'italic' }}>Question : {g.questionCandidat}</div>}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Argumentaire + fiche candidat */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {argu.length > 0 && (
          <div style={{ ...cardBox }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '10px' }}>
              <span style={{ ...sectTitle, marginBottom: 0 }}>Argumentaire client</span>
              <button style={{ ...miniBtn, marginLeft: 'auto' }} onClick={copyArgu}><BI icon="clipboard" /> Copier</button>
            </div>
            <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '12.5px', color: 'var(--text)', lineHeight: 1.55 }}>
              {argu.map((a, i) => <li key={i} style={{ marginBottom: '4px' }}>{a}</li>)}
            </ul>
          </div>
        )}
        {Array.isArray(prep.fiche_candidat?.points_a_creuser) && prep.fiche_candidat.points_a_creuser.length > 0 && (
          <div style={{ ...cardBox }}>
            <div style={sectTitle}>Fiche candidat — points à creuser</div>
            {prep.fiche_candidat.points_a_creuser.map((p, i) => (
              <div key={i} style={{ padding: '8px 0', borderTop: i === 0 ? 'none' : '1px solid var(--hair)' }}>
                <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)' }}>{p.point}</div>
                {p.question_probable && <div style={{ fontSize: '11.5px', color: 'var(--text-2)', marginTop: '2px' }}>Q : {p.question_probable}</div>}
                {p.trame_reponse && <div style={{ fontSize: '11.5px', color: 'var(--text-3)', marginTop: '2px' }}>Trame : {p.trame_reponse}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ————————————————————————————————————————————————————————————————
// Composant racine : liste des préparations → lancement → run → vue
export default function CVPrep({ candidateId, besoinId, positions, onClose, showNotification, theme = 'light' }) {
  const [mode, setMode] = useState('list'); // list | run | view
  const [preps, setPreps] = useState(null);
  const [prepId, setPrepId] = useState(null);
  const [prep, setPrep] = useState(null);
  const [run, setRun] = useState({ status: 'pending', step: 'extraction', error: '' });
  const [pickBesoin, setPickBesoin] = useState(besoinId || '');
  const [launching, setLaunching] = useState(false);
  const pollRef = useRef(null);

  const loadList = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/cv-prep?candidate_id=${candidateId}`, { headers: authH() });
      const j = await r.json().catch(() => ({}));
      setPreps(j.data || j.preparations || (Array.isArray(j) ? j : []));
    } catch { setPreps([]); }
  }, [candidateId]);

  useEffect(() => { if (candidateId != null) loadList(); }, [candidateId, loadList]);

  const loadPrep = useCallback(async (id) => {
    const r = await fetch(`${BASE}/cv-prep/${id}`, { headers: authH() });
    const j = await r.json().catch(() => ({}));
    return j.data || j;
  }, []);

  // Polling pendant le run
  const startPolling = useCallback((id) => {
    clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const d = await loadPrep(id);
        setRun({ status: d.run_status || 'running', step: d.run_step || 'extraction', error: d.run_error || '' });
        if (d.run_status === 'done') { clearInterval(pollRef.current); setPrep(d); setMode('view'); }
        else if (d.run_status === 'error') { clearInterval(pollRef.current); }
      } catch { /* on retente au prochain tick */ }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
  }, [loadPrep]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  // Besoin du run courant — nécessaire pour « Relancer » (= re-POST), y compris
  // sur une préparation existante ouverte depuis la liste.
  const runBesoinRef = useRef(null);
  const [cvMissing, setCvMissing] = useState(false); // fiche sans CV source → lancement bloqué

  // La préparation part du CV ORIGINAL de la fiche : sans CV source, on ne lance pas
  // (sinon le pipeline extrait du vide). En cas de doute (erreur réseau), on laisse
  // le backend trancher.
  const hasSourceCv = async () => {
    try {
      const r = await fetch(`${BASE}/candidates/${candidateId}/documents`, { headers: authH() });
      if (!r.ok) return true;
      const j = await r.json().catch(() => ({}));
      const arr = j.documents || j.data?.documents || (Array.isArray(j.data) ? j.data : null) || (Array.isArray(j) ? j : []);
      return (Array.isArray(arr) ? arr : []).some(d =>
        d.is_principal === true
        || (/^cv/i.test(d.type_piece || '') && d.source !== 'generation' && d.source !== 'cv_prep' && !/format[ _]?entreprise|cv[_ ]?tat/i.test(`${d.label || ''} ${d.name || d.original_name || ''}`)));
    } catch { return true; }
  };

  // Lance (ou relance) une préparation : POST /cv-prep → nouveau run à poller.
  const launchFor = async (bId) => {
    if (!bId) { showNotification?.('Choisis un besoin.', 'error'); return; }
    setLaunching(true);
    try {
      if (!(await hasSourceCv())) {
        setCvMissing(true); setMode('list');
        showNotification?.('Aucun CV sur la fiche candidat — uploadez d\'abord un CV.', 'error');
        return;
      }
      setCvMissing(false);
      const r = await fetch(`${BASE}/cv-prep`, { method: 'POST', headers: jsonH(), body: JSON.stringify({ candidateId, besoinId: bId }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      runBesoinRef.current = bId;
      setPrepId(j.id); setMode('run'); setRun({ status: j.run_status || 'running', step: 'extraction', error: '' });
      startPolling(j.id);
    } catch (e) { showNotification?.(`Échec du lancement : ${e.message}`, 'error'); }
    finally { setLaunching(false); }
  };
  const launch = () => launchFor(pickBesoin);

  const openExisting = async (id) => {
    setPrepId(id);
    const d = await loadPrep(id).catch(() => null);
    if (!d) { showNotification?.('Préparation introuvable.', 'error'); return; }
    runBesoinRef.current = d.besoin_id ?? d.besoinId ?? runBesoinRef.current;
    if (d.run_status && d.run_status !== 'done') { setMode('run'); setRun({ status: d.run_status, step: d.run_step || 'extraction', error: d.run_error || '' }); startPolling(id); }
    else { setPrep(d); setMode('view'); }
  };

  const reload = async () => { const d = await loadPrep(prepId); setPrep(d); };
  // « Relancer » = re-POST (le run est en erreur côté serveur ; re-poller ne suffit pas).
  const retry = () => {
    clearInterval(pollRef.current);
    const bId = runBesoinRef.current || pickBesoin;
    if (!bId) { showNotification?.('Besoin du run introuvable — relance depuis la liste.', 'error'); setMode('list'); loadList(); return; }
    launchFor(bId);
  };

  const besoinsOptions = (positions || []).map(p => ({ id: p.besoin_id ?? p.besoinId ?? p.id, titre: p.besoin_titre ?? p.titre ?? p.poste ?? `Besoin ${p.besoin_id ?? p.id}`, client: p.client || p.besoin_client }))
    .filter(o => o.id != null);

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(4,6,11,0.6)', backdropFilter: 'blur(2px)', zIndex: 600, display: 'flex', justifyContent: 'flex-end' };
  const panel = { width: 'min(1080px, 96vw)', background: 'var(--bg, #0b0c11)', height: '100%', display: 'flex', flexDirection: 'column' };

  return (
    <div style={overlay} onClick={onClose}>
      <div className="ats-scope" data-theme={theme} style={panel} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <BI icon="file-earmark-text" style={{ color: 'var(--brand)', fontSize: '17px' }} />
          <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-strong)' }}>Optimisation du CV</span>
          {mode !== 'list' && <button onClick={() => { clearInterval(pollRef.current); setMode('list'); setPrep(null); setPrepId(null); loadList(); }} style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-2)', background: 'none', border: 'none', cursor: 'pointer' }}><BI icon="arrow-left" /> Optimisations</button>}
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: '17px' }}><BI icon="x-lg" /></button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {mode === 'list' && (
            <div style={{ maxWidth: '620px', margin: '0 auto' }}>
              <div style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '12px', padding: '18px', marginBottom: '18px' }}>
                <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--text-strong)', marginBottom: '10px' }}>Nouvelle optimisation</div>
                {cvMissing && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', padding: '10px 12px', borderRadius: '9px', border: '1px solid rgba(226,35,26,0.30)', background: 'rgba(226,35,26,0.06)', marginBottom: '12px' }}>
                    <BI icon="file-earmark-x" style={{ color: 'var(--brand)', flex: 'none', marginTop: '1px' }} />
                    <div style={{ fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
                      <strong style={{ color: 'var(--text-strong)' }}>Aucun CV sur la fiche candidat.</strong> L'optimisation part du CV original : uploadez un CV sur la fiche, puis relancez.
                    </div>
                  </div>
                )}
                <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-2)' }}>Besoin cible</label>
                <ErpiaSelect value={pickBesoin} onChange={e => setPickBesoin(e.target.value)}
                  style={{ width: '100%', marginTop: '5px', padding: '9px 11px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '13px', outline: 'none' }}>
                  <option value="">— Choisir un besoin —</option>
                  {besoinsOptions.map(o => <option key={o.id} value={o.id}>{o.titre}{o.client ? ` · ${o.client}` : ''}</option>)}
                </ErpiaSelect>
                <button className="ats-btn ats-btn-primary" disabled={launching || !pickBesoin} onClick={launch}
                  style={{ marginTop: '12px', padding: '10px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, opacity: (launching || !pickBesoin) ? 0.6 : 1 }}>
                  <BI icon={launching ? 'arrow-repeat' : 'magic'} style={launching ? { animation: 'ats-spin .8s linear infinite' } : undefined} /> {launching ? 'Lancement…' : 'Optimiser le CV'}
                </button>
              </div>

              <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: '10px' }}>Optimisations existantes</div>
              {preps == null ? <div style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Chargement…</div>
                : preps.length === 0 ? <div style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucune préparation pour ce candidat.</div>
                : preps.map(p => {
                  const s = STATUS[p.status] || STATUS.DRAFT;
                  return (
                    <button key={p.id} onClick={() => openExisting(p.id)} style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 13px', border: '1px solid var(--border)', borderRadius: '9px', background: 'var(--panel)', cursor: 'pointer', marginBottom: '7px', fontFamily: 'inherit' }}>
                      <BI icon="file-earmark-text" style={{ color: 'var(--brand)', flex: 'none' }} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>{p.besoin_titre || p.poste || `Besoin ${p.besoin_id || ''}`}</span>
                        <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--text-2)' }}>Score {fmtScore(p.score_before)} → {fmtScore(p.score_after)}</span>
                      </span>
                      <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '6px', background: s.bg, color: s.fg, flex: 'none' }}>{s.label}</span>
                    </button>
                  );
                })}
            </div>
          )}

          {mode === 'run' && <Stepper step={run.step} status={run.status} error={run.error} onRetry={retry} retrying={launching} />}

          {mode === 'view' && prep && <PrepView prep={prep} prepId={prepId} onReload={reload} showNotification={showNotification} />}
        </div>
      </div>
    </div>
  );
}
